import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Modal that lets an admin grant paid access without going through Stripe
 * — used for Enterprise deals and custom B2B contracts closed by the
 * Sollute commercial team.
 *
 * Props:
 *   - user: { id, email, name, plan_label, paid_until, is_trial }  (required)
 *   - onClose():    callback to dismiss
 *   - onSuccess():  callback after the grant succeeds (triggers parent reload)
 */
const PAYMENT_METHODS = [
  { v: "contrato",     l: "Contrato anual" },
  { v: "boleto",       l: "Boleto bancário" },
  { v: "pix",          l: "PIX manual" },
  { v: "transferencia", l: "Transferência / TED" },
  { v: "outro",        l: "Outro" },
];

export default function GrantAccessModal({ user, onClose, onSuccess }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    plan_tier: "ate_20",
    plan_label: "",
    max_lives: 20,
    months: 12,
    amount_brl: "",
    payment_method: "contrato",
    notes: "",
    send_welcome_email: true,
  });

  useEffect(() => {
    api.get("/pricing/plans")
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
  }, []);

  const isCustom = form.plan_tier === "custom";
  const isPaid = !user.is_trial && user.paid_until && new Date(user.paid_until) > new Date();
  const paidUntilBr = isPaid ? new Date(user.paid_until).toLocaleDateString("pt-BR") : null;

  const submit = async (e) => {
    e.preventDefault();
    if (isCustom && (!form.plan_label || !form.max_lives)) {
      toast.error("Plano personalizado exige rótulo e número máximo de colaboradores.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        plan_tier: form.plan_tier,
        months: Number(form.months) || 12,
        payment_method: form.payment_method,
        notes: form.notes || null,
        send_welcome_email: form.send_welcome_email,
      };
      if (isCustom) {
        payload.plan_label = form.plan_label.trim();
        payload.max_lives = Number(form.max_lives);
      }
      if (form.amount_brl !== "" && Number(form.amount_brl) >= 0) {
        payload.amount_brl = Number(form.amount_brl);
      }
      const { data } = await api.post(`/admin/users/${user.id}/grant-access`, payload);
      const stacked = data.stacked ? " (somado ao acesso atual)" : "";
      const mailMsg = form.send_welcome_email
        ? (data.welcome_email_delivered
            ? " · e-mail de boas-vindas enviado"
            : " · ⚠️ e-mail não pôde ser entregue (verifique Resend)")
        : "";
      toast.success(
        `Plano liberado até ${new Date(data.paid_until).toLocaleDateString("pt-BR")}${stacked}${mailMsg}`,
        { duration: 7000 }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const monthsPreview = () => {
    const base = isPaid ? new Date(user.paid_until) : new Date();
    const target = new Date(base);
    target.setMonth(target.getMonth() + (Number(form.months) || 0));
    return target.toLocaleDateString("pt-BR");
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="grant-access-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
        data-testid="grant-access-modal"
      >
        <div className="overline mb-2 text-emerald-700">Comercial Sollute</div>
        <h2 className="font-display text-2xl font-extrabold mb-1 tracking-tight">Liberar plano manualmente</h2>
        <p className="text-sm text-slate-600 mb-2">
          Para clientes adquiridos diretamente pelo comercial (fora do Stripe).
          Registra a transação para o dashboard financeiro como <strong>fora Stripe</strong>.
        </p>
        <div className="mb-6 p-3 border border-slate-200 bg-slate-50 text-xs text-slate-700">
          <div><strong>Usuário:</strong> {user.name} <span className="text-slate-500 font-mono">({user.email})</span></div>
          {isPaid
            ? <div className="text-emerald-700 mt-1"><i className="fa-solid fa-circle-check mr-1"></i> Já é cliente pagante — <strong>{user.plan_label || user.plan_tier}</strong> até {paidUntilBr}. Os meses adicionados <strong>serão somados</strong> a essa data.</div>
            : <div className="text-amber-700 mt-1"><i className="fa-solid fa-circle-info mr-1"></i> Conta atualmente em demonstração / sem plano ativo.</div>}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label">Plano contratado *</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {plans.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setForm((f) => ({ ...f, plan_tier: p.id, max_lives: p.max_lives, plan_label: p.label }))}
                  data-testid={`grant-plan-${p.id}`}
                  className={`text-left p-3 border transition-colors ${
                    form.plan_tier === p.id ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                >
                  <div className="font-bold text-slate-900 text-sm">{p.label}</div>
                  <div className="text-[10px] text-slate-500 mt-1">Até {p.max_lives} colaboradores · R$ {(p.amount * 12).toFixed(2).replace(".", ",")}/ano</div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, plan_tier: "custom" }))}
                data-testid="grant-plan-custom"
                className={`text-left p-3 border transition-colors ${
                  isCustom ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-400 bg-white"
                }`}
              >
                <div className="font-bold text-slate-900 text-sm">Personalizado</div>
                <div className="text-[10px] text-slate-500 mt-1">Enterprise / acordo customizado</div>
              </button>
            </div>
          </div>

          {isCustom && (
            <>
              <div>
                <label className="label">Rótulo do plano *</label>
                <input
                  required
                  value={form.plan_label}
                  onChange={(e) => setForm({ ...form, plan_label: e.target.value })}
                  placeholder="Enterprise 500 vidas"
                  className="input"
                  data-testid="grant-plan-label"
                />
              </div>
              <div>
                <label className="label">Máx. colaboradores *</label>
                <input
                  required
                  type="number"
                  min={1}
                  value={form.max_lives}
                  onChange={(e) => setForm({ ...form, max_lives: e.target.value })}
                  className="input"
                  data-testid="grant-max-lives"
                />
              </div>
            </>
          )}

          <div>
            <label className="label">Duração (meses) *</label>
            <input
              required
              type="number"
              min={1}
              max={120}
              value={form.months}
              onChange={(e) => setForm({ ...form, months: e.target.value })}
              className="input"
              data-testid="grant-months"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Acesso liberado até <strong>{monthsPreview()}</strong>
              {isPaid && <span className="text-emerald-700"> (somado à data atual)</span>}
            </div>
          </div>

          <div>
            <label className="label">Valor cobrado (R$)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.amount_brl}
              onChange={(e) => setForm({ ...form, amount_brl: e.target.value })}
              placeholder="Opcional — alimenta dashboard financeiro"
              className="input"
              data-testid="grant-amount"
            />
          </div>

          <div className="md:col-span-2">
            <label className="label">Forma de pagamento</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setForm({ ...form, payment_method: m.v })}
                  data-testid={`grant-method-${m.v}`}
                  className={`text-xs font-semibold p-2 border transition-colors ${
                    form.payment_method === m.v ? "border-emerald-700 bg-emerald-50 text-emerald-900" : "border-slate-200 hover:border-slate-400 text-slate-700"
                  }`}
                >
                  {m.l}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="label">Observações (proposta, contato, condições especiais)</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
              data-testid="grant-notes"
            />
          </div>

          <div className="md:col-span-2 p-3 border border-emerald-200 bg-emerald-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_welcome_email}
                onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                className="mt-1 h-4 w-4 accent-emerald-900"
                data-testid="grant-send-email"
              />
              <span className="text-sm text-slate-800">
                <strong>Enviar e-mail de boas-vindas</strong>
                <div className="text-xs text-slate-600 mt-1">
                  Cliente recebe e-mail branded via Resend com plano contratado, validade
                  e link para acessar a plataforma.
                </div>
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn btn-secondary" data-testid="grant-modal-cancel">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="grant-modal-save">
            {busy
              ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Liberando…</>
              : <><i className="fa-solid fa-key"></i> Liberar acesso</>}
          </button>
        </div>
      </form>
    </div>
  );
}
